import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardWrapper from '../components/DashboardWrapper';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Heart, Flag, Download, Users, Clock, Package, ThumbsUp, Smile,
  Calendar, Tag, Building2, Truck, Box, Scale, FileText,
  Percent, Calculator, Hash, Info,
  Star, Pencil, X, ArrowUpDown, ExternalLink, Palette,
  Video, LinkIcon, Megaphone, CheckCircle, Gift, TrendingUp,
  ChevronLeft, ChevronRight, ZoomIn, Globe, Languages, Layers,
  Home, MessageSquare, Cake, Dices, Ruler, Target,
  PackageMinus, PackagePlus, Boxes, Edit2, Check, Copy, Trash2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import TagList from '../components/TagList';
import { getNormalizedTags } from '../lib/tagEngine';
import { RosetteIcon } from '../components/PartnerBadges';
import { resolveProduct } from '../domain/product/resolveProduct';
import DistributionAttachForm from '../components/forms/DistributionAttachForm';
import DistributionCard from '../components/DistributionCard';
import ComparisonTable from '../components/ComparisonTable';
import { useCart } from '../context/CartContext';
import QuickAddModal from '../components/QuickAddModal';
import { getEligibleDistributors } from '../lib/eligibility';

// ============================================================================
// HERO CTA — Opens QuickAddModal or shows ineligible label
// ============================================================================
const HeroCTA = ({ product }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const eligibleDists = getEligibleDistributors(product);

  if (eligibleDists.length === 0) {
    return (
      <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="hero-no-commercial">
        <p className="text-xs text-slate-400 text-center">Pas de distributeur partenaire commercial</p>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="hero-cta-add">
      <button
        onClick={() => setModalOpen(true)}
        className="w-full h-10 flex items-center justify-center gap-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
        data-testid="hero-add-btn"
      >
        <Calculator className="h-4 w-4" /> Ajouter
      </button>
      {modalOpen && <QuickAddModal product={product} open={modalOpen} onClose={() => setModalOpen(false)} />}
    </div>
  );
};

/**
 * ProductDetailPage V4.4 — Clean Architecture & Visual Coherence
 * 
 * HERO: Titre → Distributeur → Pricing → KPIs sociaux → CTA (réduits)
 * OFFRES ET PROMOTIONS: Précommande (blue) + Promo référence (red) + Offres (orange) — aligné badges image
 * Tab PRODUIT: 2 colonnes strictes sans cards, description courte + longue en bas
 * Tab LOGISTIQUE ET DISPONIBILITÉ: KPIs + Table distributeurs (disponibilité par distributeur)
 * Tab MARKETING ET CONTENU: Pourquoi référencer + Atouts + Médias
 * Tab AVIS: Avis professionnels
 */

/**
 * ACCESS RULE PARTNER (source de vérité unique)
 * - Distributor → toujours cliquable
 * - Autres → cliquable uniquement si au moins un User est associé au Partner
 */
import { isPartnerClickable, PARTNER_DISABLED_TOOLTIP, PARTNER_ACTIVE_CLASSES, PARTNER_DISABLED_CLASSES } from '../lib/partnerPolicy';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { user, activeOrg, hasPermission } = useAuth();
  const [product, setProduct] = useState(null);
  const currentProductIdRef = useRef(id);
  currentProductIdRef.current = id;
  const [comments, setComments] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(0);
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hasCommented, setHasCommented] = useState(false);
  const [userComment, setUserComment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ commentaire: '', rating: 0, title: '' });
  const [sortBy, setSortBy] = useState('recent');
  const [imageOrder, setImageOrder] = useState([0, 1, 2, 3, 4]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [favoriteComment, setFavoriteComment] = useState(null);
  const [editingFavComment, setEditingFavComment] = useState(false);
  const [favCommentDraft, setFavCommentDraft] = useState('');
  const [savingFavComment, setSavingFavComment] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // === PARTNER DISTRIBUTION MANAGEMENT ===
  const [editingDist, setEditingDist] = useState(null); // distribution object to edit
  const [editDistSubmitting, setEditDistSubmitting] = useState(false);
  const [deletingDist, setDeletingDist] = useState(null); // distribution object to delete

  const isPartner = user?.role === 'partenaire';
  const isAdmin = user?.role === 'admin';
  // Multi-org : org active pour déterminer les distributions "propres"
  const currentOrgId = activeOrg?.org_id;
  const orgIdRef = useRef(currentOrgId);
  useEffect(() => { orgIdRef.current = currentOrgId; }, [currentOrgId]);
  const partnerDistId = currentOrgId; // remplace user?.partner_id
  const canManageDist = hasPermission('distribution.manage');

  const handleEditDistSubmit = async (productId, data) => {
    if (!editingDist) return;
    setEditDistSubmitting(true);
    try {
      await api.put(`/products/${productId}/distributions/${editingDist.id}`, data);
      toast.success('Distribution mise à jour');
      setEditingDist(null);
      loadProduct(); // refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setEditDistSubmitting(false);
    }
  };

  const handleDeleteDist = async () => {
    if (!deletingDist || !product) return;
    try {
      await api.delete(`/products/${product.id}/distributions/${deletingDist.id}`);
      toast.success('Distribution supprimée');
      setDeletingDist(null);
      loadProduct(); // refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCopyProductLink = async () => {
    const link = `/dashboard/produit/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  const emojis = ['😀', '😊', '😍', '🤔', '👍', '👎', '❤️', '🔥', '⭐', '👏', '🎉', '💯'];

  useEffect(() => {
    setProduct(null);
    setLoading(true);
    loadProduct();
    loadComments();
    checkFavoriteStatus();
    loadProductComment();
    checkUserComment();
  }, [id]);

  useEffect(() => {
    if (product?.id) {
      loadActiveOffers();
    }
  }, [product?.id]);

  const loadProduct = async () => {
    const requestedId = id;
    try {
      const response = await api.get(`/products/${requestedId}?_ts=${Date.now()}`);
      if (requestedId !== currentProductIdRef.current) return;
      setProduct(response.data);
    } catch {
      if (requestedId === currentProductIdRef.current) {
        toast.error('Produit non trouvé');
      }
    } finally {
      if (requestedId === currentProductIdRef.current) {
        setLoading(false);
      }
    }
  };

  const getVisibleOffers = (currentProduct, allOffers) => {
    if (!currentProduct?.id || !allOffers?.length) return [];
    return allOffers.filter(offer => {
      if (!offer.is_active) return false;
      const scopeType = offer.scope_type || (offer.product_ids?.length > 0 ? 'PRODUCTS' : 'PARTNER');
      if (scopeType === 'PRODUCTS') return offer.product_ids?.includes(currentProduct.id);
      if (scopeType === 'PARTNER') return currentProduct.partner_id && offer.partner_id === currentProduct.partner_id;
      if (offer.apply_to_all_products && currentProduct.partner_id) return offer.partner_id === currentProduct.partner_id;
      return false;
    });
  };

  const loadActiveOffers = async () => {
    try {
      const response = await api.get('/offers/public/active');
      setActiveOffers(getVisibleOffers(product, response.data || []));
    } catch {
      setActiveOffers([]);
    }
  };

  const loadComments = async (sort = sortBy) => {
    try {
      const response = await api.get(`/comments/${id}?sort=${sort}`);
      if (response.data?.comments) {
        setComments(response.data.comments || []);
        setReviewStats(response.data.stats || null);
      } else {
        setComments(Array.isArray(response.data) ? response.data : []);
        setReviewStats(null);
      }
    } catch {
      setComments([]);
      setReviewStats(null);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    try {
      const response = await api.get(`/favorites?user_id=${user.id}&product_id=${id}`);
      const favs = response.data || [];
      setIsFavorite(favs.some(f => f.type === 'favori'));
      setIsFollowing(favs.some(f => f.type === 'a_suivre'));
    } catch {}
  };

  // Charger le commentaire produit depuis product_comments (source de vérité V2)
  const loadProductComment = async () => {
    if (!user) return;
    try {
      const response = await api.get(`/product-comments/${id}`);
      setFavoriteComment(response.data?.comment || null);
    } catch {
      setFavoriteComment(null);
    }
  };

  const toggleFavorite = async (type) => {
    if (!user) return;
    try {
      const isActive = (type === 'favori' && isFavorite) || (type === 'a_suivre' && isFollowing);
      if (isActive) {
        await api.delete(`/favorites?user_id=${user.id}&product_id=${id}&type=${type}`);
        if (type === 'favori') { setIsFavorite(false); }
        if (type === 'a_suivre') { setIsFollowing(false); }
        toast.success(type === 'favori' ? 'Retiré de la Wishlist' : 'Retiré des produits suivis');
      } else {
        await api.post('/favorites', { user_id: user.id, product_id: id, type });
        if (type === 'favori') setIsFavorite(true);
        if (type === 'a_suivre') setIsFollowing(true);
        toast.success(type === 'favori' ? 'Ajouté à la Wishlist' : 'Ajouté aux produits suivis');
      }
      loadProduct();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const saveFavoriteComment = async () => {
    setSavingFavComment(true);
    try {
      await api.put('/product-comments', { product_id: id, comment: favCommentDraft.trim() || null });
      setFavoriteComment(favCommentDraft.trim() || null);
      setEditingFavComment(false);
      toast.success('Commentaire mis à jour');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingFavComment(false); }
  };

  const checkUserComment = async () => {
    try {
      const response = await api.get(`/comments/${id}/user-comment`);
      if (response.data?.comment) { setHasCommented(true); setUserComment(response.data.comment); }
      else { setHasCommented(false); setUserComment(null); }
    } catch { setHasCommented(false); setUserComment(null); }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await api.post('/comments', { product_id: id, commentaire: newComment, rating: newRating > 0 ? newRating : null, title: newTitle.trim() || null });
      toast.success('Avis ajouté !');
      setNewComment(''); setNewRating(0); setNewTitle('');
      setHasCommented(true); checkUserComment(); loadComments();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleEditComment = () => {
    if (userComment) { setEditData({ commentaire: userComment.commentaire || '', rating: userComment.rating || 0, title: userComment.title || '' }); setIsEditing(true); }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/comments/${userComment.id}`, { commentaire: editData.commentaire, rating: editData.rating > 0 ? editData.rating : null, title: editData.title.trim() || null });
      toast.success('Avis modifié !'); setIsEditing(false); checkUserComment(); loadComments();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la modification'); }
  };

  const handleCancelEdit = () => { setIsEditing(false); setEditData({ commentaire: '', rating: 0, title: '' }); };
  const handleSortChange = (newSort) => { setSortBy(newSort); loadComments(newSort); };
  const handleMarkUseful = async (commentId) => { try { await api.post(`/comments/${commentId}/useful`); loadComments(); } catch {} };

  const StarRating = ({ rating, onRatingChange, readonly = false, size = 'md' }) => {
    const sizeClasses = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button key={star} type="button" onClick={() => !readonly && onRatingChange && onRatingChange(star)} disabled={readonly}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}>
            <Star className={`${sizeClasses[size]} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
          </button>
        ))}
      </div>
    );
  };

  // === HELPERS ===
  const formatDate = (dateStr) => { if (!dateStr) return null; try { return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return dateStr; } };
  const formatDateShort = (dateStr) => { if (!dateStr) return null; try { return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return dateStr; } };

  const productTags = getNormalizedTags(product);

  // === LOADING / ERROR STATES ===
  if (loading) return (<DashboardWrapper><div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div></div></DashboardWrapper>);
  if (!product) return (<DashboardWrapper><div className="bg-white rounded-lg border p-12 text-center"><Package className="h-16 w-16 mx-auto text-gray-300 mb-4" /><h2 className="text-xl font-semibold mb-2">Produit non trouvé</h2><p className="text-gray-500">Ce produit n'existe pas ou a été supprimé.</p></div></DashboardWrapper>);

  // ════════════════════════════════════════════════════════════════
  // PRODUCT DOMAIN RESOLVER — Single Source of Truth (Phase 4B Clean)
  // ════════════════════════════════════════════════════════════════
  const resolved = resolveProduct(product);
  const rd = resolved.distributors;
  const rp = resolved.pricing;
  const re = resolved.editors;

  if (typeof window !== 'undefined' && window.__PRODUCT_RESOLVER_DEBUG__) {
    console.log('[RESOLVER_CLEAN]', { productId: product.id, resolved });
  }

  // ════════════════════════════════════════════════════════════════
  // COMPUTED FLAGS
  // ════════════════════════════════════════════════════════════════
  const hasPartner = Boolean(product.partner);
  const hasPrixVente = product.prix_vente_conseille != null && product.prix_vente_conseille > 0;
  const hasTva = product.tva != null;
  
  // === CONDITIONS COMMERCIALES (V4 → computed_status) ===
  const distributions = product.distributions || [];
  
  // Distributions avec mécaniques actives (computed_status = ACTIVE)
  const distPromos = distributions.filter(d => {
    const mechs = d.promotional_mechanics || [];
    return mechs.some(m => (m.computed_status || 'ARCHIVED') === 'ACTIVE');
  });
  
  // Distributions avec précommande active (Phase 3 ORU: source = promotional_mechanics)
  const distPrecommandes = distributions.filter(d => {
    const mechs = d.promotional_mechanics || [];
    return mechs.some(m => {
      if (m.nature_offre !== 'PRECOMMANDE') return false;
      if (m.computed_status) return m.computed_status === 'ACTIVE';
      // Fallback local si computed_status absent
      const s = m.date_debut, e = m.date_fin;
      if (!s && !e) return true;
      const n = new Date();
      if (s) { const sd = String(s).includes('T') ? new Date(String(s).replace('Z','+00:00')) : new Date(s+'T00:00:00+00:00'); if (n < sd) return false; }
      if (e) { const ed = String(e).includes('T') ? new Date(String(e).replace('Z','+00:00')) : new Date(e+'T23:59:59+00:00'); if (n > ed) return false; }
      return true;
    });
  });

  // Commercial
  const hasCodeEan = Boolean(product.code_ean);
  const hasCommercial = hasCodeEan;

  // Section "Conditions Commerciales" visible si au moins 1 promo, 1 précommande ou 1 offre
  const hasConditionsCommerciales = distPromos.length > 0 || distPrecommandes.length > 0 || activeOffers.length > 0;

  // Content
  const hasDescriptif = Boolean(product.descriptif);
  const hasDescriptionCourte = Boolean(product.description_courte);
  const hasListeMateriel = Boolean(product.liste_materiel);
  const hasTailleBoite = Boolean(product.taille_boite);
  const hasPoidsBoite = product.poids_boite != null;
  const hasDescription = hasDescriptif || hasListeMateriel || hasTailleBoite || hasPoidsBoite;

  // Creation
  const hasEditeur = Boolean(resolveProduct(product).editors.list.length > 0);
  const hasAuteur = Boolean(product.auteur);
  const hasIllustrateur = Boolean(product.illustrateur);
  const hasCreation = hasEditeur || hasAuteur || hasIllustrateur;

  // Availability
  const hasDateSortie = Boolean(product.date_sortie_iso);
  // Précommande: désormais distribution-level (distPrecommandes calculé plus haut)
  const hasDisponibilite = hasDateSortie || distPrecommandes.length > 0;

  // Marketing
  const hasAtouts = Boolean(product.atouts_1) || Boolean(product.atouts_2) || Boolean(product.atouts_3);
  const hasVideoUrl = Boolean(product.video_url);
  const hasRegleUrl = Boolean(product.regle_jeu_url);
  const hasPlanCom = Boolean(product.plan_communication_commercial);
  const hasPourquoiReferencer = Boolean(product.pourquoi_referencer);
  const hasMarketing = hasAtouts || hasVideoUrl || hasRegleUrl || hasPlanCom || hasPourquoiReferencer;

  // Images
  const defaultImage = 'https://images.unsplash.com/photo-1606503153255-59d8b2e4739e?w=500';
  const rawImages = product?.visuels?.length > 0 ? product.visuels.slice(0, 5) : [defaultImage];
  const mainImageIndex = imageOrder[0] < rawImages.length ? imageOrder[0] : 0;
  const mainImage = rawImages[mainImageIndex] || defaultImage;

  // Offer type label
  const getOfferTypeLabel = (offer) => {
    const types = [];
    if (offer.discount_percent) types.push('Remise');
    if (offer.franco_threshold) types.push('Franco');
    if (offer.goodies_description) types.push('Goodies');
    return types.length > 1 ? 'Offre Mixte' : types[0] || 'Offre';
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER V4
  // ════════════════════════════════════════════════════════════════
  return (
    <>
    <DashboardWrapper>
      <div className="min-h-screen bg-[#F7F5F2]">
        
        {/* ══════════ SECTION 0 — BREADCRUMB ══════════ */}
        <nav className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-2" data-testid="breadcrumb">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Link to="/dashboard" className="hover:text-blue-600 transition-colors flex items-center gap-1">
                <Home className="h-3 w-3" />
                Accueil
              </Link>
              <span>/</span>
              <span className="text-gray-800 font-medium truncate max-w-[200px]">{product.intitule}</span>
            </div>
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyProductLink}
                className="h-7 px-2.5 text-xs text-gray-500 hover:text-gray-800 gap-1.5 transition-colors"
                data-testid="copy-product-link-btn"
              >
                {linkCopied ? (
                  <><Check className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-600">Copié</span></>
                ) : (
                  <><Copy className="h-3.5 w-3.5" /><span className="hidden sm:inline">Copier le lien</span></>
                )}
              </Button>
            )}
          </div>
        </nav>

        {/* ══════════ SECTION 1 — HERO PRODUIT ══════════ */}
        <section className="bg-white border-b border-gray-100" data-testid="section-header">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              
              {/* GALERIE */}
              <div className="w-full lg:w-[45%] flex-shrink-0">
                {/* Desktop Gallery */}
                <div className="hidden md:flex gap-3">
                  {rawImages.length > 1 && (
                    <div className="flex flex-col gap-2 w-[72px] flex-shrink-0">
                      {[0, 1, 2, 3, 4].map((idx) => {
                        const imgSrc = rawImages[idx];
                        if (!imgSrc) return (
                          <div key={`ph-${idx}`} className="w-[72px] h-[72px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center">
                            <Package className="h-4 w-4 text-gray-300" />
                          </div>
                        );
                        return (
                          <button key={idx} onClick={() => setImageOrder([idx, ...imageOrder.filter(i => i !== idx)])}
                            className={`w-[72px] h-[72px] rounded-lg overflow-hidden border-2 transition-all ${mainImageIndex === idx ? 'border-gray-900 ring-2 ring-gray-900/20' : 'border-gray-200 hover:border-gray-400'}`}
                            data-testid={`product-thumbnail-${idx}`}>
                            <img src={imgSrc} alt={`Vue ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = defaultImage; }} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex-1 relative aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200 group">
                    <img src={mainImage} alt={product.intitule || 'Produit'} className="w-full h-full object-contain p-4 cursor-zoom-in" data-testid="product-main-image"
                      onClick={() => { setLightboxIndex(mainImageIndex); setShowLightbox(true); }} onError={(e) => { e.target.src = defaultImage; }} />
                    {productTags.length > 0 && <div className="absolute top-3 left-3 z-10"><TagList tags={productTags} variant="detail" /></div>}
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="h-3 w-3" /> Agrandir
                    </div>
                    {rawImages.length > 1 && <div className="absolute top-3 right-3 bg-black/70 text-white px-2.5 py-1 rounded-full text-xs">{mainImageIndex + 1}/{rawImages.length}</div>}
                  </div>
                </div>
                {/* Mobile Gallery */}
                <div className="md:hidden space-y-3">
                  <div className="relative aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200"
                    onClick={() => { setLightboxIndex(mainImageIndex); setShowLightbox(true); }}>
                    <img src={mainImage} alt={product.intitule || 'Produit'} className="w-full h-full object-contain p-3" data-testid="product-main-image-mobile" onError={(e) => { e.target.src = defaultImage; }} />
                    {productTags.length > 0 && <div className="absolute top-3 left-3"><TagList tags={productTags} variant="detail" /></div>}
                    {rawImages.length > 1 && <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs">{mainImageIndex + 1}/{rawImages.length}</div>}
                  </div>
                  {rawImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {rawImages.map((imgSrc, idx) => (
                        <button key={idx} onClick={() => setImageOrder([idx, ...imageOrder.filter(i => i !== idx)])}
                          className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${mainImageIndex === idx ? 'border-gray-900' : 'border-gray-200'}`}>
                          <img src={imgSrc} alt={`Vue ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = defaultImage; }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* HEADER INFO — V4.4.1 MICRO-HIÉRARCHIE */}
              <div className="flex-1 min-w-0 flex flex-col">
                {/* ── GROUPE IDENTITÉ ── */}
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight" data-testid="product-title">
                  {product.intitule || 'Produit sans nom'}
                </h1>
                {/* ── BLOC PRICING — PVC comme prix de référence produit ── */}
                <div className="mt-5 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="pricing-section">
                  <div className="flex flex-wrap items-baseline gap-3">
                    {hasPrixVente ? (
                      <>
                        <span className="font-black text-gray-900 text-4xl" data-testid="product-price">
                          {product.prix_vente_conseille.toFixed(2)} €
                        </span>
                        <span className="text-gray-400 text-sm">PVC</span>
                        <span className="text-gray-400 text-sm">TTC</span>
                        {hasTva && hasPrixVente && <span className="text-gray-400 text-sm">(TVA {product.tva}%)</span>}
                      </>
                    ) : (
                      <span className="text-gray-400 text-lg italic" data-testid="product-price">Prix NC</span>
                    )}

                  </div>
                </div>

                {/* ── TABLEAU DISTRIBUTEURS (tous, sans hiérarchie) ── */}
                {(product.distributions || []).length > 0 && !(user?.role === 'retailer' && user?.account_status === 'a_completer') && (
                  <div className="mt-4" data-testid="hero-distributors-table">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Distributeur</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Prix achat</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Disponibilité</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {product.distributions.map((dist, idx) => (
                          <tr key={dist.id || idx} data-testid={`hero-dist-row-${idx}`}>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1.5">
                                {isPartnerClickable(dist) && dist.partner_slug ? (
                                  <Link to={`/partner/${dist.partner_slug}`} className={`text-sm font-semibold ${PARTNER_ACTIVE_CLASSES}`} data-testid={`dist-link-${idx}`}>
                                    {dist.partner_nom || 'Distributeur'}
                                  </Link>
                                ) : (
                                  <span className={`text-sm font-medium ${dist.partner_slug ? PARTNER_DISABLED_CLASSES : 'text-gray-900'}`} title={dist.partner_slug ? PARTNER_DISABLED_TOOLTIP : undefined} aria-disabled={dist.partner_slug && !isPartnerClickable(dist) ? 'true' : undefined}>{dist.partner_nom || 'Distributeur'}</span>
                                )}
                                {dist.partner_is_commercial && <RosetteIcon className="w-4 h-4 flex-shrink-0" />}
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              {dist.purchase_price != null ? (
                                <span className="font-bold text-gray-900">{dist.purchase_price.toFixed(2)}€</span>
                              ) : (
                                <span className="text-gray-400 italic text-xs">N/C</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                dist.availability_status === 'available' ? 'bg-green-50 text-green-700 border border-green-200' :
                                dist.availability_status === 'unavailable' ? 'bg-red-50 text-red-700 border border-red-200' :
                                'bg-gray-50 text-gray-500 border border-gray-200'
                              }`}>
                                {dist.availability_status === 'available' ? 'En stock' :
                                 dist.availability_status === 'unavailable' ? 'Indisponible' : 'N/C'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Disponibilité globale supprimée — voir uniquement le tableau distributeurs ci-dessus */}

                {/* ── CTA HERO — Opens QuickAddModal (Phase 4G — multiple UX entry points) ── */}
                {user?.role === 'retailer' && user?.account_status !== 'a_completer' && distributions.length > 0 && (
                  <HeroCTA product={product} />
                )}
                {user?.role === 'retailer' && user?.account_status === 'a_completer' && distributions.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-center" data-testid="hero-cta-blocked">
                    <p className="text-xs text-amber-700">Complétez votre profil pour accéder aux conditions de distribution.</p>
                  </div>
                )}

                {/* ── GROUPE SOCIAL + ACTIONS (centré, unifié) ── */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 w-full text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1.5"><Heart className="h-4 w-4 text-red-400" /><strong className="text-gray-800">{product.nb_favoris || 0}</strong> ajouts à la Wishlist</span>
                    <span className="flex items-center gap-1.5"><Flag className="h-4 w-4 text-blue-400" /><strong className="text-gray-800">{product.nb_a_suivre || 0}</strong> suivis</span>
                    {reviewStats?.average_rating && (
                      <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /><strong className="text-gray-800">{reviewStats.average_rating}</strong> ({reviewStats.total_reviews} avis)</span>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-4 w-full" data-testid="cta-buttons">
                    <Button size="sm" variant="outline"
                      className={`h-9 text-sm px-5 ${isFavorite ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => toggleFavorite('favori')} data-testid="toggle-favorite-button">
                      <Heart className={`h-4 w-4 mr-1.5 ${isFavorite ? 'fill-current' : ''}`} />
                      {isFavorite ? 'Dans la Wishlist' : 'Ajouter à la Wishlist'}
                    </Button>
                    <Button size="sm" variant="ghost"
                      className={`h-9 text-sm px-4 ${isFollowing ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'}`}
                      onClick={() => toggleFavorite('a_suivre')} data-testid="toggle-follow-button">
                      <Flag className={`h-3.5 w-3.5 mr-1.5 ${isFollowing ? 'fill-current' : ''}`} />
                      {isFollowing ? 'Suivi' : 'Suivre'}
                    </Button>
                  </div>

                  {/* ── COMMENTAIRE PRODUIT (visible si favori OU suivi OU commentaire historique) ── */}
                  {(isFavorite || isFollowing || favoriteComment) && (
                    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="favorite-comment-bloc">
                      {editingFavComment ? (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-gray-500">Mon commentaire</label>
                          <Textarea
                            value={favCommentDraft}
                            onChange={(e) => setFavCommentDraft(e.target.value)}
                            placeholder="Ajouter un commentaire personnel..."
                            rows={2}
                            className="text-sm bg-white"
                            data-testid="favorite-comment-textarea"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => setEditingFavComment(false)} disabled={savingFavComment}>
                              <X className="h-3.5 w-3.5 mr-1" /> Annuler
                            </Button>
                            <Button size="sm" onClick={saveFavoriteComment} disabled={savingFavComment} data-testid="favorite-comment-save">
                              <Check className="h-3.5 w-3.5 mr-1" /> {savingFavComment ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <MessageSquare className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            {favoriteComment ? (
                              <p className="text-sm text-gray-700" data-testid="favorite-comment-text">{favoriteComment}</p>
                            ) : (
                              <p className="text-sm text-gray-400 italic">Aucun commentaire</p>
                            )}
                          </div>
                          <button
                            onClick={() => { setFavCommentDraft(favoriteComment || ''); setEditingFavComment(true); }}
                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                            data-testid="favorite-comment-edit"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* LIGHTBOX MODAL */}
        {showLightbox && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setShowLightbox(false)} data-testid="lightbox-modal">
            <button onClick={() => setShowLightbox(false)} className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"><X className="h-8 w-8" /></button>
            {rawImages.length > 1 && (<>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + rawImages.length) % rawImages.length); }} className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"><ChevronLeft className="h-8 w-8" /></button>
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % rawImages.length); }} className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"><ChevronRight className="h-8 w-8" /></button>
            </>)}
            <img src={rawImages[lightboxIndex] || defaultImage} alt={`${product.intitule} - Vue ${lightboxIndex + 1}`} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
            {rawImages.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {rawImages.map((_, idx) => (
                  <button key={idx} onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${idx === lightboxIndex ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* MAIN CONTENT — DECISION LAYER + EXPLORATION LAYER */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

          {/* ══════════ OFFRES PONCTUELLES (collection Offers — hors distributions) ══════════ */}
          {activeOffers.length > 0 && (
            <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="section-conditions-commandes">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-gray-400" />
                  Offres Ponctuelles
                </h2>
              </div>
              <div className="p-4 space-y-3">
                {activeOffers.map((offer) => (
                  <div key={offer.id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="h-4 w-4 text-amber-700" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Offre Ponctuelle</span>
                        </div>
                        <p className="font-semibold text-amber-900">{offer.title || 'Offre Ponctuelle'}</p>
                        {offer.offer_code && (
                          <span className="font-mono text-[10px] text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">{offer.offer_code}</span>
                        )}
                      </div>
                      {(offer.start_date || offer.end_date) && (
                        <p className="text-xs text-amber-600 flex-shrink-0">
                          {offer.start_date && `Du ${formatDateShort(offer.start_date)}`}
                          {offer.end_date && ` au ${formatDateShort(offer.end_date)}`}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm mb-3">
                      {offer.discount_percent && <span className="text-amber-800"><strong>-{offer.discount_percent}%</strong> remise</span>}
                      {offer.franco_threshold && <span className="text-amber-800">Franco: <strong>{offer.franco_threshold}{'\u20AC'}</strong></span>}
                      {offer.goodies_description && <span className="text-amber-800">Goodies: {offer.goodies_description}</span>}
                    </div>
                    <Link to={`/offre/${offer.id}`}
                      className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow transition-all" data-testid={`link-offer-${offer.id}`}>
                      <ExternalLink className="h-4 w-4" /> Voir le détail
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ══════════ EXPLORATION LAYER — TABS ══════════ */}
          <Tabs defaultValue="produit" className="w-full" data-testid="product-tabs">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <TabsList className="w-full h-auto p-1 bg-gray-100 border-b border-gray-200 rounded-none flex overflow-x-auto gap-1" data-testid="tabs-list">
                <TabsTrigger value="produit" className="flex-1 min-w-[100px] rounded-md border border-transparent data-[state=active]:border-gray-300 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 data-[state=active]:font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900 py-2.5 px-4 text-sm font-medium gap-2 transition-all" data-testid="tab-produit">
                  <Info className="h-4 w-4" />
                  <span className="hidden sm:inline">Produit</span>
                  <span className="sm:hidden">Produit</span>
                </TabsTrigger>
                <TabsTrigger value="logistique" className="flex-1 min-w-[100px] rounded-md border border-transparent data-[state=active]:border-gray-300 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 data-[state=active]:font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900 py-2.5 px-4 text-sm font-medium gap-2 transition-all" data-testid="tab-logistique">
                  <Calendar className="h-4 w-4" />
                  <span className="hidden sm:inline">Conditions de Distribution</span>
                  <span className="sm:hidden">Distributions</span>
                </TabsTrigger>
                {hasMarketing && (
                  <TabsTrigger value="marketing" className="flex-1 min-w-[100px] rounded-md border border-transparent data-[state=active]:border-gray-300 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 data-[state=active]:font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900 py-2.5 px-4 text-sm font-medium gap-2 transition-all" data-testid="tab-marketing">
                    <Megaphone className="h-4 w-4" />
                    <span className="hidden sm:inline">Marketing et Contenu</span>
                    <span className="sm:hidden">Marketing</span>
                  </TabsTrigger>
                )}
                <TabsTrigger value="avis" className="flex-1 min-w-[100px] rounded-md border border-transparent data-[state=active]:border-gray-300 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 data-[state=active]:font-semibold text-gray-700 hover:bg-gray-200 hover:text-gray-900 py-2.5 px-4 text-sm font-medium gap-2 transition-all" data-testid="tab-avis">
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Avis</span>
                  <span className="sm:hidden">Avis</span>
                  {reviewStats?.total_reviews > 0 && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full data-[state=active]:bg-gray-900 data-[state=active]:text-white">{reviewStats.total_reviews}</span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* ════════ TAB PRODUIT — V4.4.1 (colonnes indépendantes, icônes métier) ════════ */}
              <TabsContent value="produit" className="mt-0 p-4 sm:p-5" data-testid="tab-content-produit">
                {/* Fiche technique — 2 colonnes INDÉPENDANTES */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10" data-testid="section-caracteristiques">
                  {/* COLONNE 1 */}
                  <div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Hash className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">EAN</span><span className="font-mono font-medium text-gray-900">{product.code_ean || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Date de sortie</span><span className="font-medium text-gray-900">{product.date_sortie_iso ? formatDate(product.date_sortie_iso) : '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Languages className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Langue</span><span className="font-medium text-gray-900">{
                        Array.isArray(product.langue) && product.langue.length > 0
                          ? product.langue.map(code => (
                              <span key={code} className={`inline-block px-1.5 py-0.5 rounded text-xs mr-1 ${code === 'SANS_LANGUE' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}>
                                {code === 'SANS_LANGUE' ? 'Sans langue' : code}
                              </span>
                            ))
                          : typeof product.langue === 'string' && product.langue
                            ? product.langue
                            : '—'
                      }</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Users className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Joueurs</span><span className="font-medium text-gray-900">{product.nb_joueurs_min != null || product.nb_joueurs_max != null ? `${product.nb_joueurs_min ?? '?'} – ${product.nb_joueurs_max ?? '?'}` : '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Cake className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Âge minimum</span><span className="font-medium text-gray-900">{product.age_minimum != null ? `${product.age_minimum}+` : '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Durée</span><span className="font-medium text-gray-900">{product.temps_moyen_partie != null ? `${product.temps_moyen_partie} min` : '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Target className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Public</span><span className="font-medium text-gray-900">{product.public_cible || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Tag className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Type de jeu</span><span className="font-medium text-gray-900">{product.type_jeu || '—'}</span>
                    </div>
                    <div className="py-2.5 flex items-start gap-2.5 text-sm">
                      <Layers className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" /><span className="text-gray-500 w-32 flex-shrink-0">Tags Produit</span>
                      {product.tags_produit_resolved && product.tags_produit_resolved.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {product.tags_produit_resolved.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              title={tag.category ? `Catégorie : ${tag.category}` : undefined}
                              data-testid={`tag-produit-${tag.id}`}
                            >
                              {tag.label || tag.value}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="font-medium text-gray-900">{product.mecanisme || '—'}</span>
                      )}
                    </div>
                  </div>

                  {/* COLONNE 2 — indépendante en hauteur */}
                  <div>
                    {/* Éditeur — avec lien conditionnel (règle d'accès unique) */}
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500 w-32 flex-shrink-0">Éditeur</span>
                      {(() => {
                        const pubs = re.originalPublishers || re.list || [];
                        if (pubs.length === 0) return <span className="font-medium text-gray-900">{re.displayLabel}</span>;
                        return pubs.map((pub, idx) => {
                          const isClickable = isPartnerClickable(pub);
                          const slug = pub.partner_slug || pub.slug;
                          return (
                            <span key={pub.id || idx} className="inline-flex items-center gap-1">
                              {idx > 0 && <span className="text-gray-300 mx-1">/</span>}
                              {isClickable && slug ? (
                                <Link to={`/partner/${slug}`} className={`font-medium ${PARTNER_ACTIVE_CLASSES}`} data-testid={`publisher-link-${idx}`}>
                                  {pub.name}
                                </Link>
                              ) : (
                                <span className={`font-medium ${PARTNER_DISABLED_CLASSES}`} title={PARTNER_DISABLED_TOOLTIP} aria-disabled="true" data-testid={`publisher-disabled-${idx}`}>
                                  {pub.name}
                                </span>
                              )}
                            </span>
                          );
                        });
                      })()}
                    </div>
                    {re.mode === 'multi' && (
                      <div className="ml-9 py-1.5 mb-0.5" data-testid="multi-editors-section">
                        <div className="p-2 bg-gray-50 rounded border border-gray-100 space-y-1">
                          {(re.originalPublishers || re.list).map((ed, idx) => {
                            const isClickable = isPartnerClickable(ed);
                            const slug = ed.partner_slug || ed.slug;
                            return (
                              <div key={ed.id || idx} className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                                <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                {isClickable && slug ? (
                                  <Link to={`/partner/${slug}`} className={PARTNER_ACTIVE_CLASSES} data-testid={`multi-pub-link-${idx}`}>
                                    {ed.name || 'Éditeur'}
                                  </Link>
                                ) : (
                                  <span className={PARTNER_DISABLED_CLASSES} title={PARTNER_DISABLED_TOOLTIP} aria-disabled="true">{ed.name || 'Éditeur'}</span>
                                )}
                                {ed.country && <span className="text-gray-400">({ed.country})</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Auteur</span><span className="font-medium text-gray-900">{product.auteur || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Palette className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Illustrateur</span><span className="font-medium text-gray-900">{product.illustrateur || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-start gap-2.5 text-sm">
                      <Dices className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" /><span className="text-gray-500 w-32 flex-shrink-0">Contenu boîte</span><span className="font-medium text-gray-900 whitespace-pre-line">{product.liste_materiel || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Ruler className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Taille</span><span className="font-medium text-gray-900">{product.taille_boite || '—'}</span>
                    </div>
                    <div className="py-2.5 border-b border-gray-100 flex items-center gap-2.5 text-sm">
                      <Scale className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Poids</span><span className="font-medium text-gray-900">{product.poids_boite != null ? `${product.poids_boite} kg` : '—'}</span>
                    </div>
                    <div className="py-2.5 flex items-center gap-2.5 text-sm">
                      <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" /><span className="text-gray-500 w-32 flex-shrink-0">Origine</span><span className="font-medium text-gray-900">{product.pays_provenance || '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Description courte puis longue — typographie alignée sur le tableau */}
                <div className="pt-4 mt-4 border-t border-gray-200" data-testid="section-description">
                  {hasDescriptionCourte && (
                    <div className="mb-4" data-testid="product-short-desc">
                      <div className="py-2.5 flex items-start gap-2.5 text-sm">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-500 w-32 flex-shrink-0">Description courte</span>
                        <span className="font-medium text-gray-900 leading-relaxed">{product.description_courte}</span>
                      </div>
                    </div>
                  )}
                  {hasDescriptif && (
                    <div>
                      <div className="py-2.5 flex items-start gap-2.5 text-sm">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-500 w-32 flex-shrink-0">Description</span>
                        <span className="font-medium text-gray-900 leading-relaxed whitespace-pre-line">{product.descriptif}</span>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* ════════ TAB CONDITIONS DE DISTRIBUTION ════════ */}
              <TabsContent value="logistique" className="mt-0 p-4 sm:p-5" data-testid="tab-content-logistique">
                
                {/* Blocage pour retailers avec profil incomplet */}
                {user?.role === 'retailer' && user?.account_status === 'a_completer' ? (
                  <div className="text-center py-10 px-4" data-testid="distribution-access-blocked">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                      <Lock className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">Accès restreint</h3>
                    <p className="text-sm text-gray-600 max-w-md mx-auto mb-4">
                      Votre profil doit être complété pour accéder aux conditions de distribution.
                      Rendez-vous dans Mon compte pour renseigner les informations obligatoires.
                    </p>
                    <a
                      href="/dashboard/profil"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                      data-testid="distribution-blocked-link"
                    >
                      Compléter mon profil
                      <ArrowUpDown className="h-3.5 w-3.5" />
                    </a>
                  </div>
                ) : (
                  <>
                    {(product.distributions || []).length > 0 ? (
                      <div className="space-y-4" data-testid="section-distributions-cards">
                        {product.distributions.map((dist, idx) => (
                          <DistributionCard
                            key={dist.id || idx}
                            dist={dist}
                            idx={idx}
                            isPartner={isPartner}
                            isOwn={isPartner && dist.partner_id === partnerDistId}
                            canManage={canManageDist}
                            onEdit={setEditingDist}
                            onDelete={setDeletingDist}
                            userRole={user?.role}
                            productId={product.id}
                            productName={product.intitule}
                            productImage={(product.images || [])[0]}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-6">Aucune condition de distribution renseignée.</p>
                    )}

                    {/* COMPARISON TABLE — Retailer only, AFTER distributions */}
                    {!isPartner && !isAdmin && (product.distributions || []).length > 0 && (
                      <div className="mt-6">
                        <ComparisonTable productId={product.id} productName={product.intitule} productImage={(product.images || [])[0]} />
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* ════════ TAB MARKETING ET CONTENU — V4.3 ════════ */}
              {hasMarketing && (
                <TabsContent value="marketing" className="mt-0 p-4 sm:p-5" data-testid="tab-content-marketing">
                  <div data-testid="section-marketing">
                    {/* Pourquoi référencer ce jeu — V4.3 nouveau champ */}
                    {hasPourquoiReferencer && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="pourquoi-referencer">
                        <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2 flex items-center gap-2">
                          <Megaphone className="h-4 w-4" /> Pourquoi référencer ce jeu ?
                        </h3>
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{product.pourquoi_referencer}</p>
                      </div>
                    )}

                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Megaphone className="h-4 w-4 text-gray-400" /> Marketing et Contenu
                    </h3>
                    {hasAtouts && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        {product.atouts_1 && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><div className="flex items-center gap-1.5 mb-1"><CheckCircle className="h-4 w-4 text-gray-400" /><span className="text-xs text-gray-500">Atout 1</span></div><p className="text-sm font-medium text-gray-800">{product.atouts_1}</p></div>
                        )}
                        {product.atouts_2 && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><div className="flex items-center gap-1.5 mb-1"><CheckCircle className="h-4 w-4 text-gray-400" /><span className="text-xs text-gray-500">Atout 2</span></div><p className="text-sm font-medium text-gray-800">{product.atouts_2}</p></div>
                        )}
                        {product.atouts_3 && (
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><div className="flex items-center gap-1.5 mb-1"><CheckCircle className="h-4 w-4 text-gray-400" /><span className="text-xs text-gray-500">Atout 3</span></div><p className="text-sm font-medium text-gray-800">{product.atouts_3}</p></div>
                        )}
                      </div>
                    )}
                    {(hasVideoUrl || hasRegleUrl) && (
                      <div className="flex flex-wrap gap-3 mb-4">
                        {hasVideoUrl && <a href={product.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors text-sm"><Video className="h-4 w-4" />Voir la Vidéo des Règles<ExternalLink className="h-3 w-3" /></a>}
                        {hasRegleUrl && <a href={product.regle_jeu_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors text-sm"><LinkIcon className="h-4 w-4" />Règles du jeu<ExternalLink className="h-3 w-3" /></a>}
                      </div>
                    )}
                    {hasPlanCom && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Plan média et commercial</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{product.plan_communication_commercial}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}

              {/* ════════ TAB AVIS ════════ */}
              <TabsContent value="avis" className="mt-0 p-4 sm:p-5" data-testid="tab-content-avis">
                <div data-testid="section-avis">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4 text-gray-400" />
                      Avis des professionnels
                      {reviewStats?.total_reviews > 0 && <span className="text-xs font-normal text-gray-400 normal-case">({reviewStats.total_reviews})</span>}
                    </h3>
                    {comments.length > 1 && (
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} className="text-sm border rounded-md px-2 py-1 bg-white" data-testid="sort-comments">
                          <option value="recent">Plus récents</option>
                          <option value="useful">Plus utiles</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Synthèse avis */}
                  {reviewStats && reviewStats.total_reviews > 0 ? (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="review-summary">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="flex flex-col items-center lg:items-start">
                          <div className="flex items-baseline gap-2 mb-2">
                            <span className="text-4xl font-bold text-gray-900">{reviewStats.average_rating || '—'}</span>
                            <span className="text-lg text-gray-400">/ 5</span>
                          </div>
                          {reviewStats.average_rating && <div className="mb-2"><StarRating rating={Math.round(reviewStats.average_rating)} readonly size="md" /></div>}
                          <p className="text-sm text-gray-500">{reviewStats.total_rated > 0 ? `Basé sur ${reviewStats.total_rated} avis noté${reviewStats.total_rated > 1 ? 's' : ''}` : 'Aucun avis noté'}</p>
                        </div>
                        <div className="lg:border-x lg:px-6 border-gray-200">
                          <p className="text-sm font-medium mb-3 text-gray-700">Répartition des notes</p>
                          <div className="space-y-2">
                            {[5, 4, 3, 2, 1].map((star) => {
                              const count = reviewStats.rating_distribution?.[star] || 0;
                              const total = reviewStats.total_rated || 1;
                              const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                              return (
                                <div key={star} className="flex items-center gap-2 text-sm">
                                  <span className="w-8 text-right font-medium text-gray-600">{star}★</span>
                                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-yellow-400 rounded-full" style={{ width: `${percent}%` }} /></div>
                                  <span className="w-6 text-xs text-gray-400">{count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="space-y-3">
                          {reviewStats.most_useful && reviewStats.most_useful.nb_utile > 0 && (
                            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                              <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> Avis le plus utile</p>
                              <p className="text-sm text-gray-700 line-clamp-2">"{reviewStats.most_useful.title || reviewStats.most_useful.commentaire?.substring(0, 80)}..."</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
                      <Star className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">Aucun avis pour ce produit</p>
                      <p className="text-sm text-gray-400 mt-1">Soyez le premier à donner votre avis !</p>
                    </div>
                  )}

                  {/* Formulaire / État existant */}
                  {!hasCommented ? (
                    <form onSubmit={handleAddComment} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium mb-4 text-gray-800">Donnez votre avis</p>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-gray-700">Votre note</label>
                        <div className="flex items-center gap-3">
                          <StarRating rating={newRating} onRatingChange={setNewRating} size="lg" />
                          {newRating > 0 && (<><span className="text-sm font-medium">{newRating}/5</span><button type="button" onClick={() => setNewRating(0)} className="text-xs text-gray-400 hover:text-gray-600 underline">Effacer</button></>)}
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1 text-gray-700">Titre (optionnel)</label>
                        <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Ex: Excellent jeu" className="bg-white" maxLength={100} data-testid="comment-title-input" />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1 text-gray-700">Votre avis</label>
                        <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Partagez votre expérience..." rows={4} className="bg-white" data-testid="comment-text-input" />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1"><Smile className="h-4 w-4" /> Emoji</button>
                        <Button type="submit" disabled={!newComment.trim()} data-testid="submit-comment-btn">Publier</Button>
                      </div>
                      {showEmojiPicker && (
                        <div className="flex flex-wrap gap-1 p-2 mt-2 bg-white rounded border">{emojis.map((e, i) => (<button key={i} type="button" onClick={() => setNewComment(p => p + e)} className="text-xl hover:scale-125 p-1">{e}</button>))}</div>
                      )}
                    </form>
                  ) : (
                    <div className="mb-4">
                      {isEditing ? (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between mb-4"><h3 className="font-medium text-blue-800">Modifier votre avis</h3><button onClick={handleCancelEdit} className="text-blue-600 hover:text-blue-800"><X className="h-5 w-5" /></button></div>
                          <div className="mb-4"><label className="block text-sm font-medium mb-2">Note</label><StarRating rating={editData.rating} onRatingChange={(r) => setEditData({ ...editData, rating: r })} size="lg" /></div>
                          <div className="mb-4"><label className="block text-sm font-medium mb-1">Titre</label><Input value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} className="bg-white" maxLength={100} /></div>
                          <div className="mb-4"><label className="block text-sm font-medium mb-1">Avis</label><Textarea value={editData.commentaire} onChange={(e) => setEditData({ ...editData, commentaire: e.target.value })} rows={4} className="bg-white" /></div>
                          <div className="flex justify-end gap-2"><Button variant="outline" onClick={handleCancelEdit}>Annuler</Button><Button onClick={handleSaveEdit}>Enregistrer</Button></div>
                        </div>
                      ) : (
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Vous avez déjà donné votre avis</p>
                            <Button variant="ghost" size="sm" onClick={handleEditComment} className="text-emerald-700 hover:text-emerald-900" data-testid="edit-my-comment-btn"><Pencil className="h-4 w-4 mr-1" /> Modifier</Button>
                          </div>
                          {userComment && (
                            <div className="mt-2 p-3 bg-white rounded border border-emerald-200">
                              {userComment.rating && <div className="mb-1"><StarRating rating={userComment.rating} readonly size="sm" /></div>}
                              {userComment.title && <p className="font-semibold text-sm mb-1">{userComment.title}</p>}
                              <p className="text-sm text-gray-600 line-clamp-2">{userComment.commentaire}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Liste des avis */}
                  <div className="space-y-3" data-testid="comments-list">
                    {comments.length === 0 ? (
                      <p className="text-gray-400 text-center py-4">Aucun avis pour le moment.</p>
                    ) : comments.map((c) => {
                      const isMostUseful = reviewStats?.most_useful?.id === c.id && c.nb_utile > 0;
                      const isMostRecent = reviewStats?.most_recent?.id === c.id && !isMostUseful;
                      return (
                        <div key={c.id} className={`border rounded-lg p-4 transition-colors ${isMostUseful ? 'bg-emerald-50 border-emerald-200' : isMostRecent ? 'bg-blue-50 border-blue-200' : 'border-gray-200 hover:bg-gray-50'}`} data-testid={`comment-${c.id}`}>
                          {(isMostUseful || isMostRecent) && (
                            <div className="mb-2"><span className={`text-xs font-semibold px-2 py-1 rounded ${isMostUseful ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{isMostUseful ? 'Avis le plus utile' : 'Avis le plus récent'}</span></div>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <p className="font-semibold text-gray-900">{c.user_name || 'Anonyme'}</p>
                                {c.user_id === user?.id && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Vous</span>}
                              </div>
                              {c.rating && <div className="mb-1"><StarRating rating={c.rating} readonly size="sm" /></div>}
                              <p className="text-xs text-gray-400">{formatDate(c.created_at)}</p>
                            </div>
                            <button onClick={() => handleMarkUseful(c.id)} disabled={c.users_utile?.includes(user?.id) || c.user_id === user?.id}
                              className={`flex items-center gap-1 text-sm px-2 py-1 rounded ${c.users_utile?.includes(user?.id) ? 'text-blue-600 bg-blue-50' : c.user_id === user?.id ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                              <ThumbsUp className={`h-4 w-4 ${c.users_utile?.includes(user?.id) ? 'fill-current' : ''}`} /><span>{c.nb_utile || 0} utile{(c.nb_utile || 0) > 1 ? 's' : ''}</span>
                            </button>
                          </div>
                          {c.title && <p className="font-medium mb-1 text-gray-800">{c.title}</p>}
                          <p className="whitespace-pre-line text-sm text-gray-600">{c.commentaire}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          {/* Bouton téléchargement — conditionnel */}
          {product.download_fiche_enabled && (
            <div className="flex justify-center">
              <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="btn-download-fiche"
                onClick={async () => {
                  try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    const token = localStorage.getItem('token');
                    const dlOrgId = orgIdRef.current;
                    const orgParam = dlOrgId ? `?org_id=${encodeURIComponent(dlOrgId)}` : '';
                    const response = await fetch(`${backendUrl}/api/products/${product.id}/export-excel${orgParam}`, {
                      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                    });
                    if (!response.ok) {
                      const err = await response.json().catch(() => ({}));
                      toast.error(err.detail || 'Accès refusé');
                      return;
                    }
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const disposition = response.headers.get('Content-Disposition');
                    a.download = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') || 'fiche_produit.xlsx' : 'fiche_produit.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Téléchargement en cours...');
                  } catch (e) {
                    toast.error('Erreur lors du téléchargement');
                  }
                }}>
                <Download className="h-4 w-4 mr-2" /> Télécharger la fiche produit
              </Button>
            </div>
          )}

          {/* Footer meta */}
          <footer className="text-xs text-gray-400 border-t border-gray-200 pt-4 flex flex-wrap items-center justify-between gap-3" data-testid="footer-meta">
            <div className="flex items-center gap-3">
              {product.created_at && <span>Créé le {formatDate(product.created_at)}</span>}
              {product.updated_at && <span>Mis à jour le {formatDate(product.updated_at)}</span>}
            </div>
            <span className={`flex items-center gap-1 ${product.is_active ? 'text-emerald-500' : 'text-red-400'}`}>
              {product.is_active ? 'Actif' : 'Inactif'}
            </span>
          </footer>
        </main>
      </div>
    </DashboardWrapper>

    {/* === PARTNER: Edit Distribution Modal === */}
    {isPartner && editingDist && product && (
      <DistributionAttachForm
        open={!!editingDist}
        onClose={() => setEditingDist(null)}
        product={{
          id: product.id,
          intitule: product.intitule,
          code_ean: product.code_ean,
          editeur: resolveProduct(product).editors.displayLabel || '',
          image: product.visuels?.[0] || null
        }}
        onSubmit={handleEditDistSubmit}
        submitting={editDistSubmitting}
        initialData={editingDist}
        editMode={true}
      />
    )}

    {/* === PARTNER: Delete Distribution Confirmation === */}
    <AlertDialog open={!!deletingDist} onOpenChange={(v) => { if (!v) setDeletingDist(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer votre distribution</AlertDialogTitle>
          <AlertDialogDescription>
            Votre offre commerciale pour ce produit sera définitivement supprimée. Les distributions des autres partenaires ne seront pas affectées. Cette action est irréversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="dist-delete-cancel">Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteDist}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="dist-delete-confirm"
          >
            Supprimer ma distribution
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default ProductDetailPage;
